/**
 * The dish list, from the server.
 *
 * The page is the server's — `GET /api/v1/workspace-foods/` takes `page` and
 * `page_size`, so turning a page refetches.
 *
 * The search is not. That endpoint has no search param yet, so the box narrows
 * the page in hand rather than asking for a narrower one, and this hook says
 * so plainly instead of pretending otherwise: `searchScope` comes back as
 * `"page"`, and the screen tells the reader which rows were looked through.
 * The day the endpoint takes a `q`, the filter below becomes a query param and
 * the debounce is already here for it — the screen above need not change.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { DEFAULT_PAGE_SIZE, fetchDishesPage } from "../services/dishesService";
import type { Dish } from "../types/dishes.types";

/** Long enough that typing a name is one pass, not eight. */
const SEARCH_DEBOUNCE_MS = 300;

export type DishesStatus = "loading" | "ready" | "failed";

export interface DishesState {
  /** The rows to draw — one page, narrowed by the search if there is one. */
  dishes: Dish[];
  /** How many the workspace holds in all, whatever the search says. */
  count: number;
  /**
   * How wide the search reached. `"page"` while one is typed, because that is
   * all the endpoint lets it reach; `"none"` when the box is empty and every
   * row on the page is showing.
   */
  searchScope: "none" | "page";
  /** Rows the current page held before the search narrowed them. */
  pageSize: number;
  status: DishesStatus;
  /** Set only when `status` is `failed` — what the screen shows. */
  error?: string;
  /** Runs the call again. For a Try again button, not for every keystroke. */
  reload: () => void;
}

export interface UseDishesOptions {
  /** Debounced here, so a call site can pass the box's value directly. */
  search?: string;
  /** 1-based, the way the endpoint counts. Changing it refetches. */
  page?: number;
  /** How many rows a page holds. Defaults to the service's ten. */
  pageSize?: number;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

const NO_DISHES: Dish[] = [];

/** Case- and space-insensitive, so "egg  curry" still finds "Egg curry". */
const normalise = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");

export function useDishes({
  search = "",
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseDishesOptions = {}): DishesState {
  const getToken = useTokenGetter();

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const isFirstSearch = useRef(true);

  useEffect(() => {
    // The first pass must not wait — the screen would otherwise open on a
    // blank list for no reason, since there is nothing typed to debounce.
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      setDebouncedSearch(search);
      return;
    }

    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useQuery({
    // The search is not in the key: it does not change what is fetched, only
    // which of the fetched rows are drawn. Putting it there would throw away a
    // page per keystroke and refetch the same one.
    queryKey: ["dishes", "list", page, pageSize] as const,
    queryFn: ({ signal }) => fetchDishesPage(getToken, { page, pageSize, signal }),
    // The rows already on screen stay while the next page is fetched — cached
    // across mounts too, so a list visited again shows its last page instantly
    // while a fresh read runs behind it.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isError) console.error("Dishes failed to load:", query.error);
  }, [query.isError, query.error]);

  // Back to nothing on a real failure: a row from a page that is no longer
  // confirmed current is worse than none.
  const fetched = query.isError ? NO_DISHES : query.data?.dishes ?? NO_DISHES;

  const term = normalise(debouncedSearch);

  const dishes = useMemo(
    () => (term ? fetched.filter((dish) => normalise(dish.name).includes(term)) : fetched),
    [fetched, term]
  );

  return {
    dishes,
    count: query.isError ? 0 : query.data?.count ?? 0,
    searchScope: term ? "page" : "none",
    pageSize: fetched.length,
    status: query.isError ? "failed" : query.isFetching ? "loading" : "ready",
    error: query.isError ? failureMessage(query.error) : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}
